// React Imports
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

// Mui Imports
import MenuItem from "@mui/material/MenuItem";
import {
    DialogContentText,
    Input,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Select,
    Stack
} from "@mui/material";

// App Imports
import Box from "@mui/material/Box";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { changeWorkQueue } from "../../workQueueSlice";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DynamicCdpvValueField from "../../../CommonComponents/DynamicCdpvValueField";
import FormControl from "@mui/material/FormControl";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
    getTestScenario
} from "../../../ContractDataPointValue/ContractDataPointValueClient";


// main function
export default function TestContract({ onMenuClose, rowContractData }) {

    // ****************** BEGIN DISPATCH ****************************

    const dispatchData = useDispatch();

    // ****************** END DISPATCH ****************************

    const controlsPropertyId = useSelector(
        (state) => state.workQueueSlc.value.mousedContractPropertyId
    );

    const controlsTestScenarioDefaultPropertyId = useSelector(
        (state) => state.workQueueSlc.value.testScenarioDefaultPropertyId
    );

    const controlsTestScenarioPeriodEnd = useSelector(
        (state) => state.workQueueSlc.value.testScenarioPeriodEnd
    );

    const controlsTestScenarioPropertyId = useSelector(
        (state) => state.workQueueSlc.value.testScenarioPropertyId
    );

    const controlsTestScenarioList = useSelector(
        (state) => state.workQueueSlc.value.testScenarioList
    );

    const controlsTestScenarioNumber = useSelector(
        (state) => state.workQueueSlc.value.testScenarioNumber
    );

    const controlsTestScenarioName = useSelector(
        (state) => state.workQueueSlc.value.testScenarioName
    );

    const controlsTestScenarioMaxNumber = useSelector(
        (state) => state.workQueueSlc.value.testScenarioMaxNumber
    );

    const controlsTestScenarioSelectedNumber = useSelector(
        (state) => state.workQueueSlc.value.testScenarioSelectedNumber
    );


    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [scenarioData, setScenarioData] = React.useState([]);

    /*
     * Keep a stable Dayjs object for the DatePicker.
     *
     * Constructing dayjs(controlsTestScenarioPeriodEnd) directly inside the
     * DatePicker creates a new object whenever this component rerenders.
     * That can cause the calendar to return to the selected date's month while
     * the user is navigating through other months.
     */
    const [testPeriodEndValue, setTestPeriodEndValue] = React.useState(() => {
        if (!controlsTestScenarioPeriodEnd) {
            return null;
        }

        const initialValue = dayjs(controlsTestScenarioPeriodEnd);

        return initialValue.isValid()
            ? initialValue
            : null;
    });


    /*
     * Synchronize the local picker value when Redux is changed externally,
     * but do not replace the local Dayjs object when the date is unchanged.
     */
    React.useEffect(() => {
        const nextValue = controlsTestScenarioPeriodEnd
            ? dayjs(controlsTestScenarioPeriodEnd)
            : null;

        setTestPeriodEndValue((currentValue) => {
            if (!currentValue && !nextValue) {
                return currentValue;
            }

            if (
                currentValue &&
                nextValue &&
                nextValue.isValid() &&
                currentValue.isSame(nextValue, "day")
            ) {
                return currentValue;
            }

            return nextValue && nextValue.isValid()
                ? nextValue
                : null;
        });
    }, [controlsTestScenarioPeriodEnd]);


    const onTest = () => {
        onMenuClose();

        console.log("onTest triggered");
        console.log("controlsPropertyId = ", controlsPropertyId);

        getTestScenario(controlsPropertyId)
            .then((data) => {
                console.log("test scenario data fetched");
                console.log(data);

                setScenarioData(data);

                const systemPeriod = data[0]?.systemPeriod ?? null;
                const systemPeriodValue = systemPeriod
                    ? dayjs(systemPeriod)
                    : null;

                /*
                 * Update the local DatePicker value immediately, in addition
                 * to updating Redux.
                 */
                setTestPeriodEndValue(
                    systemPeriodValue && systemPeriodValue.isValid()
                        ? systemPeriodValue
                        : null
                );

                dispatchData(
                    changeWorkQueue({
                        testScenarioPeriodEnd: systemPeriod
                    })
                );

                const defaultPropertyId = data.find(
                    (d) => d.dataPointId === 11612
                )?.value;

                console.log(
                    "data defaultPropertyId = ",
                    defaultPropertyId
                );

                dispatchData(
                    changeWorkQueue({
                        testScenarioDefaultPropertyId: defaultPropertyId
                    })
                );

                const testScenarios = [
                    ...new Set(
                        data
                            .filter((d) => d.dataPointId === 11614)
                            .map((d) => Number(d.value))
                            .filter((value) => !Number.isNaN(value))
                    )
                ].sort((a, b) => a - b);

                const testScenariosWithNew = [
                    ...testScenarios,
                    "(new)"
                ];

                console.log(
                    "data testScenarios = ",
                    testScenarios
                );

                dispatchData(
                    changeWorkQueue({
                        testScenarioList: testScenariosWithNew
                    })
                );

                const maxScenario = testScenarios.length
                    ? Math.max(...testScenarios)
                    : null;

                console.log(
                    "data max test scenario number = ",
                    maxScenario
                );

                dispatchData(
                    changeWorkQueue({
                        testScenarioNumber:
                            maxScenario == null
                                ? "(new)"
                                : maxScenario,
                        testScenarioMaxNumber: maxScenario
                    })
                );

                const scenarioVersionNumber = data.find(
                    (d) =>
                        d.dataPointId === 11614 &&
                        d.value === maxScenario?.toString()
                )?.versionNumber;

                const scenarioName = data.find(
                    (d) =>
                        d.dataPointId === 11615 &&
                        d.versionNumber === scenarioVersionNumber
                )?.value ?? "";

                console.log(
                    "data scenarioName = ",
                    scenarioName
                );

                dispatchData(
                    changeWorkQueue({
                        testScenarioName: scenarioName
                    })
                );

                const scenarioPropertyId = data.find(
                    (d) =>
                        d.dataPointId === 11616 &&
                        d.versionNumber === scenarioVersionNumber
                )?.value ?? defaultPropertyId;

                console.log(
                    "data controlsTestScenarioDefaultPropertyId =",
                    controlsTestScenarioDefaultPropertyId
                );

                console.log(
                    "data scenarioPropertyId =",
                    scenarioPropertyId
                );

                dispatchData(
                    changeWorkQueue({
                        testScenarioPropertyId: scenarioPropertyId
                    })
                );
            })
            .finally(() => {
                setConfirmOpen(true);

                console.log(
                    "controlsTestScenarioPeriodEnd = ",
                    controlsTestScenarioPeriodEnd
                );

                console.log(
                    "controlsTestScenarioPropertyId = ",
                    controlsTestScenarioPropertyId
                );
            });
    };


    const handleConfirmClose = () => {
        setConfirmOpen(false);
        setTestPeriodEndValue(null);
        onMenuClose();

        dispatchData(
            changeWorkQueue({
                actionMenuState: false,
                testScenarioPeriodEnd: null,
                testScenarioDefaultPropertyId: null,
                testScenarioDefaultPropertyName: null,
                testScenarioList: null,
                testScenarioNumber: null,
                testScenarioName: null
            })
        );
    };


    const handleConfirmTest = () => {
        onMenuClose();
        setConfirmOpen(false);

        const testScenarioFinalNumber =
            controlsTestScenarioNumber === "(new)"
                ? (controlsTestScenarioMaxNumber ?? 0) + 1
                : controlsTestScenarioNumber;

        dispatchData(
            changeWorkQueue({
                testScenarioSelectedNumber: testScenarioFinalNumber
            })
        );

        console.log("Testing Contract(s) from action menu");
        console.log(
            "controlsTestScenarioNumber =",
            controlsTestScenarioNumber
        );
        console.log(
            "testScenarioFinalNumber =",
            testScenarioFinalNumber
        );
        console.log(
            "controlsTestScenarioSelectedNumber =",
            controlsTestScenarioSelectedNumber
        );
        console.log(
            "controlsTestScenarioName =",
            controlsTestScenarioName
        );
        console.log(
            "controlsTestScenarioPropertyId =",
            controlsTestScenarioPropertyId
        );
        console.log(
            "controlsTestScenarioPeriodEnd =",
            controlsTestScenarioPeriodEnd
        );

        dispatchData(
            changeWorkQueue({
                actionMenuAction: "Test",
                actionMenuState: false
            })
        );
    };


    const handleObjectChange = (event) => {
        const selectedScenarioNumber = event.target.value;

        console.log(
            "handleObjectChange has been triggered with this new event: ",
            event
        );
        console.log(
            "This is for the selected Test Scenario Number"
        );
        console.log(selectedScenarioNumber);

        dispatchData(
            changeWorkQueue({
                testScenarioNumber: selectedScenarioNumber
            })
        );

        const scenarioVersionNumber = scenarioData.find(
            (d) =>
                d.dataPointId === 11614 &&
                d.value === selectedScenarioNumber.toString()
        )?.versionNumber;

        const scenarioName = scenarioData.find(
            (d) =>
                d.dataPointId === 11615 &&
                d.versionNumber === scenarioVersionNumber
        )?.value ?? "";

        console.log(
            "data scenarioName = ",
            scenarioName
        );

        dispatchData(
            changeWorkQueue({
                testScenarioName: scenarioName
            })
        );

        console.log(
            "Value has been dispatched to changeWorkQueue testScenarioName."
        );

        const scenarioPropertyId = scenarioData.find(
            (d) =>
                d.dataPointId === 11616 &&
                d.versionNumber === scenarioVersionNumber
        )?.value ?? controlsTestScenarioDefaultPropertyId;

        console.log(
            "data scenarioPropertyId = ",
            scenarioPropertyId
        );

        dispatchData(
            changeWorkQueue({
                testScenarioPropertyId: scenarioPropertyId
            })
        );

        console.log(
            "Value has been dispatched to changeWorkQueue testScenarioPropertyId."
        );
    };


    const handleTestPeriodEndChange = (newValue) => {
        /*
         * Update local state first so the DatePicker retains one stable value
         * during its internal calendar interactions.
         */
        setTestPeriodEndValue(newValue);

        /*
         * Preserve the original behavior of storing YYYY-MM-DD in Redux.
         */
        dispatchData(
            changeWorkQueue({
                testScenarioPeriodEnd:
                    newValue && newValue.isValid()
                        ? newValue.format("YYYY-MM-DD")
                        : null
            })
        );
    };


    return (
        <Box>
            {(
                (
                    rowContractData.contractLevel !== "Option" ||
                    rowContractData.contractLevel !== "Cast" ||
                    rowContractData.contractLevel !== "Engagement"
                ) &&
                rowContractData.comboStatus === "a"
            ) && (
                <Box>
                    <MenuItem onClick={onTest}>
                        <ListItemIcon>
                            <HelpOutlineIcon fontSize="small" />
                        </ListItemIcon>

                        <ListItemText>
                            Test
                        </ListItemText>
                    </MenuItem>

                    <Dialog
                        open={confirmOpen}
                        onClose={handleConfirmClose}
                        PaperProps={{
                            sx: {
                                height: 600,
                                width: 700,
                                maxWidth: "none"
                            }
                        }}
                    >
                        <DialogTitle>
                            Test Contracts
                        </DialogTitle>

                        <DialogContent>
                            <DialogContentText>
                                Continue an existing test scenario or create a new one.
                            </DialogContentText>

                            <br />
                            <br />

                            <Stack direction="row" spacing={8}>
                                <FormControl
                                    variant="standard"
                                    sx={{ width: 150 }}
                                >
                                    <InputLabel id="test-scenario-number-label">
                                        Test Scenario Number
                                    </InputLabel>

                                    <Select
                                        variant="standard"
                                        labelId="test-scenario-number-label"
                                        id="test-scenario-number"
                                        value={controlsTestScenarioNumber ?? ""}
                                        label="Test Scenario Number"
                                        onChange={handleObjectChange}
                                    >
                                        {controlsTestScenarioList?.map(
                                            (scenariosItem) => (
                                                <MenuItem
                                                    key={scenariosItem}
                                                    value={scenariosItem}
                                                >
                                                    {scenariosItem}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>

                                <Box>
                                    <InputLabel
                                        htmlFor="scenarioName"
                                        sx={{ fontSize: 12 }}
                                    >
                                        Scenario Name (optional)
                                    </InputLabel>

                                    <Input
                                        id="scenarioName"
                                        sx={{ width: 400 }}
                                        value={controlsTestScenarioName ?? ""}
                                        disabled={
                                            controlsTestScenarioNumber !== "(new)"
                                        }
                                        onChange={(event) =>
                                            dispatchData(
                                                changeWorkQueue({
                                                    testScenarioName:
                                                    event.target.value
                                                })
                                            )
                                        }
                                    />
                                </Box>
                            </Stack>

                            <br />
                            <br />

                            <DynamicCdpvValueField
                                disabled={
                                    controlsTestScenarioNumber !== "(new)"
                                }
                                selectorDataPointId={11614}
                                selectorValue={
                                    controlsTestScenarioPropertyId
                                }
                                selectorDataType="CODE"
                                selectorContext="WorkQueueTestScenarioPropertyId"
                                selectorLabel="Test Property"
                                autoFocus={false}
                            />

                            <br />
                            <br />

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Test Period End"
                                    value={testPeriodEndValue}
                                    onChange={handleTestPeriodEndChange}
                                    format="MM/DD/YYYY"
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            onClick: () =>
                                                console.log(
                                                    "raw picker clicked"
                                                )
                                        }
                                    }}
                                />
                            </LocalizationProvider>

                            <br />
                            <br />
                            <br />
                            <br />
                        </DialogContent>

                        <DialogActions>
                            <Button
                                onClick={handleConfirmClose}
                                color="primary"
                            >
                                Cancel
                            </Button>

                            <Button
                                onClick={handleConfirmTest}
                                color="primary"
                            >
                                Ok
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        </Box>
    );
}

// // React Imports
// import * as React from "react";
// import {useDispatch, useSelector} from "react-redux";
//
//
// // Mui Imports
// import MenuItem from "@mui/material/MenuItem";
// import {
//     DialogContentText, FormHelperText, Input, InputLabel,
//
//     ListItemIcon,
//     ListItemText, Select, Stack, TextField,
//     Tooltip
// } from "@mui/material";
//
//
//
// // App Imports
// import Box from "@mui/material/Box";
// import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
// import {changeWorkQueue} from "../../workQueueSlice";
// import Dialog from "@mui/material/Dialog";
// import DialogTitle from "@mui/material/DialogTitle";
// import DialogContent from "@mui/material/DialogContent";
// import DialogActions from "@mui/material/DialogActions";
// import Button from "@mui/material/Button";
// import {changeProduct} from "../../../ExternalData/Product/productSlice";
// import {changeWorkSubmitting} from "../../../Work/workSubmittingSlice";
// import {addChildContracts, advanceContract, createNewContract, getQueueC} from "../../../Contract/ContractClient";
//
// import DynamicCdpvValueField from "../../../CommonComponents/DynamicCdpvValueField";
// import FormControl from "@mui/material/FormControl";
// import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
// import {DatePicker} from "@mui/x-date-pickers/DatePicker";
// import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
// import dayjs from "dayjs";
// import {getTestScenario, getVersionInfo} from "../../../ContractDataPointValue/ContractDataPointValueClient";
//
//
// // main function
// export default function TestContract({ onMenuClose, rowContractData }) {
//
//     // ****************** BEGIN DISPATCH ****************************
//
//     const dispatchData = useDispatch();
//
//     // ****************** END DISPATCH ****************************
//
//     const controlsPropertyId = useSelector((state) => state.workQueueSlc.value.mousedContractPropertyId);
//     const controlsTestScenarioDefaultPropertyId= useSelector((state) => state.workQueueSlc.value.testScenarioDefaultPropertyId);
//     const controlsTestScenarioPeriodEnd = useSelector((state) => state.workQueueSlc.value.testScenarioPeriodEnd);
//     const controlsTestScenarioPropertyId = useSelector((state) => state.workQueueSlc.value.testScenarioPropertyId);
//     const controlsTestScenarioList = useSelector((state) => state.workQueueSlc.value.testScenarioList);
//     const controlsTestScenarioNumber = useSelector((state) => state.workQueueSlc.value.testScenarioNumber);
//     const controlsTestScenarioName = useSelector((state) => state.workQueueSlc.value.testScenarioName);
//     const controlsTestScenarioMaxNumber = useSelector((state) => state.workQueueSlc.value.testScenarioMaxNumber);
//     const controlsTestScenarioSelectedNumber = useSelector((state) => state.workQueueSlc.value.testScenarioSelectedNumber);
//
//     const [confirmOpen, setConfirmOpen] = React.useState(false);
//     const [scenarioData, setScenarioData] = React.useState([]);
//
//
//     const onTest = () => {
//         onMenuClose();
//         console.log("onTest triggered");
//         console.log("controlsPropertyId = ", controlsPropertyId);
//         getTestScenario(controlsPropertyId)
//
//             .then(data => {
//                 console.log("test scenario data fetched");
//                 console.log(data);
//                 setScenarioData(data);
//                 dispatchData(changeWorkQueue({testScenarioPeriodEnd: data[0].systemPeriod }));
//
//                 const defaultPropertyId = data.find(d => d.dataPointId === 11612)?.value;
//                 console.log("data defaultPropertyId  = ", defaultPropertyId);
//                 dispatchData(changeWorkQueue({
//                     // testScenarioPropertyId: defaultPropertyId ,
//                     testScenarioDefaultPropertyId: defaultPropertyId}));
//
//                 const testScenarios = [
//                     ...new Set(
//                         data
//                             .filter(d => d.dataPointId === 11614)
//                             .map(d => Number(d.value))
//                             .filter(v => !Number.isNaN(v)) // optional safety
//                     )
//                 ].sort((a, b) => a - b);
//                 const testScenariosWithNew = [...testScenarios, "(new)"];
//                 console.log("data testScenarios  = ", testScenarios);
//                 dispatchData(changeWorkQueue({testScenarioList: testScenariosWithNew }));
//                 // const maxScenario = testScenarios.length
//                 //     ? Math.max(...testScenarios)
//                 //     : null;
//                 const maxScenario = testScenarios.length
//                     ? Math.max(...testScenarios)
//                     : null;
//
//                 console.log("data max test scenario number  = ", maxScenario);
//
//                 dispatchData(changeWorkQueue({testScenarioNumber: maxScenario == null ? "(new)" : maxScenario, testScenarioMaxNumber: maxScenario }));
//
//                 // const scenarioVersionNumber = data.find(d => d.dataPointId === 11614 && d.value === maxScenario.toString())?.versionNumber;
//                 // const scenarioName = data.find(d => d.dataPointId === 11615 && d.versionNumber === scenarioVersionNumber)?.value;
//                 const scenarioVersionNumber = data.find(
//                     d => d.dataPointId === 11614 && d.value === maxScenario?.toString()
//                 )?.versionNumber;
//
//                 const scenarioName = data.find(
//                     d => d.dataPointId === 11615 && d.versionNumber === scenarioVersionNumber
//                 )?.value ?? "";
//                 console.log("data scenarioName  = ", scenarioName);
//                 dispatchData(changeWorkQueue({testScenarioName: scenarioName }));
//
//                 // const scenarioPropertyId = data.find(
//                 //     d => d.dataPointId === 11616 && d.versionNumber === scenarioVersionNumber
//                 // )?.value ?? controlsTestScenarioDefaultPropertyId;
//                 //
//                 // scenarioPropertyId =  scenarioPropertyId === null ? controlsTestScenarioDefaultPropertyId : scenarioPropertyId;
//                 //
//                 // console.log("data scenarioPropertyId  = ", scenarioPropertyId);
//                 // dispatchData(changeWorkQueue({testScenarioPropertyId: scenarioPropertyId }));
//
//                 let scenarioPropertyId = data.find(
//                     d => d.dataPointId === 11616 && d.versionNumber === scenarioVersionNumber
//                 )?.value ?? defaultPropertyId;
//
//                 // scenarioPropertyId =
//                 //     scenarioPropertyId === null
//                 //         ? controlsTestScenarioDefaultPropertyId
//                 //         : scenarioPropertyId;
//
//
//                 console.log("data controlsTestScenarioDefaultPropertyId =", controlsTestScenarioDefaultPropertyId);
//                 console.log("data scenarioPropertyId =", scenarioPropertyId);
//                 // const match = data.find(d =>
//                 //     Number(d.dataPointId) === 11616 &&
//                 //     Number(d.versionNumber) === Number(scenarioVersionNumber)
//                 // );
//                 //
//                 // const scenarioPropertyId =
//                 //     match?.value != null
//                 //         ? match.value
//                 //         : controlsTestScenarioDefaultPropertyId;
//                 //
//                 // console.log("data scenarioPropertyId =", scenarioPropertyId);
//
//                 dispatchData(changeWorkQueue({
//                     testScenarioPropertyId: scenarioPropertyId
//                 }));
//
//
//             })
//             .finally(() => {
//                 setConfirmOpen(true);
//                 console.log("controlsTestScenarioPeriodEnd = ", controlsTestScenarioPeriodEnd);
//                 console.log("controlsTestScenarioPropertyId = ", controlsTestScenarioPropertyId);
//             });
//     };
//
//
//
//     const handleConfirmClose = () => {
//         setConfirmOpen(false);
//         onMenuClose();
//         dispatchData(changeWorkQueue({actionMenuState: false,
//             testScenarioPeriodEnd: null,
//             testScenarioDefaultPropertyId: null,
//             testScenarioDefaultPropertyName: null,
//             testScenarioList: null,
//             testScenarioNumber: null,
//             testScenarioName: null}));
//     };
//
//
//
//     const handleConfirmTest = (rowData) => {
//         onMenuClose();
//         setConfirmOpen(false);
//         const testScenarioFinalNumber =
//             controlsTestScenarioNumber === "(new)"
//                 ? (controlsTestScenarioMaxNumber ?? 0) + 1
//                 : controlsTestScenarioNumber;
//         dispatchData(changeWorkQueue({testScenarioSelectedNumber: testScenarioFinalNumber}));
//         console.log("Testing Contract(s) from action menu");
//         console.log("controlsTestScenarioNumber =", controlsTestScenarioNumber);
//         console.log("testScenarioFinalNumber =", testScenarioFinalNumber);
//         console.log("controlsTestScenarioSelectedNumber =", controlsTestScenarioSelectedNumber);
//         console.log("controlsTestScenarioName =", controlsTestScenarioName);
//         console.log("controlsTestScenarioPropertyId =", controlsTestScenarioPropertyId);
//         console.log("controlsTestScenarioPeriodEnd =", controlsTestScenarioPeriodEnd);
//         dispatchData(changeWorkQueue({
//             actionMenuAction: "Test", actionMenuState: false
//         }))
//     }
//
//
//     const handleObjectChange = (event) => {
//         console.log("handleObjectChange has been triggered with this new event: ", event);
//         // setSelectedValue(event.target.value);
//         console.log("This is for the selected Test Scenario Number");
//         console.log(event.target.value);
//             dispatchData(changeWorkQueue({testScenarioNumber: event.target.value}));
//         const scenarioVersionNumber = scenarioData.find(
//             d => d.dataPointId === 11614 && d.value === event.target.value.toString()
//         )?.versionNumber;
//
//         const scenarioName = scenarioData.find(
//             d => d.dataPointId === 11615 && d.versionNumber === scenarioVersionNumber
//         )?.value ?? "";
//         console.log("data scenarioName  = ", scenarioName);
//         dispatchData(changeWorkQueue({testScenarioName: scenarioName }));
//             console.log("Value has been dispatched to changeWorkQueue testScenarioName.")
//
//         // if (event.target.value === "(new)")
//         // {const scenarioPropertyId = }(else)
//         const scenarioPropertyId = scenarioData.find(
//             d => d.dataPointId === 11616 && d.versionNumber === scenarioVersionNumber
//         )?.value ?? controlsTestScenarioDefaultPropertyId;
//
//         console.log("data scenarioPropertyId  = ", scenarioPropertyId);
//         dispatchData(changeWorkQueue({testScenarioPropertyId: scenarioPropertyId }));
//         console.log("Value has been dispatched to changeWorkQueue testScenarioPropertyId.")
//     }
//
//     return (
//
//         <Box>
//             {(
//                     (
//                         rowContractData.contractLevel !== "Option"
//                         || rowContractData.contractLevel !== "Cast"
//                         || rowContractData.contractLevel !== "Engagement")
//                     &&
//                     (rowContractData.comboStatus === "a")
//                 ) &&
//                 <Box>
//                 <MenuItem  onClick={onTest}
//
//                 >
//                     <ListItemIcon>
//                         <HelpOutlineIcon  fontSize="small"/>
//                     </ListItemIcon>
//                     <ListItemText>Test</ListItemText>
//                 </MenuItem>
//
//                 <Dialog
//                 // open={false} onClose={handleConfirmClose}
//                 open={confirmOpen} onClose={handleConfirmClose}
//                 PaperProps={{
//                     sx: {
//                         height: 600,
//                         width: 700,     // optional
//                         maxWidth: 'none' // IMPORTANT if you want custom width
//                     }
//                 }}
//         >
//             <DialogTitle>Test Contracts </DialogTitle>
//
//             <DialogContent>
//                 <DialogContentText>
//                     Continue an existing test scenario or create a new one.
//                 </DialogContentText>
//                 <br/>
//                 <br/>
//                 {/*{controlsTestScenarioPeriodEnd &&*/}
//                 <Stack direction="row" spacing={8}>
//                 <FormControl variant="standard"
//                     // sx={{ m: 1, minWidth: 120 }}
//                              sx={{width: 150}}
//                 >
//
//                     {/*<br/>*/}
//                     {/*<br/>*/}
//                     {/*<br/>*/}
//                     <InputLabel id="demo-simple-select-label2"
//                                 // shrink={!!selectorValue} // Shrink label when there is a value
//                     >Test Scenario Number</InputLabel>
//                     <Select
//                         variant="standard"
//                         labelId="demo-simple-select-filled-label2"
//                         id="value2"
//                         // value = {selectorValue}
//                         value = {controlsTestScenarioNumber}
//                         label="Test Scenario Number"
//                         // onChange={(ev) => setItems(ev.target.value)}
//                         onChange={handleObjectChange}
//                         // autoFocus={autoFocus}
//                     >
//                         {/*<MenuItem value="">*/}
//                         {/*    <em>None</em>*/}
//                         {/*</MenuItem>*/}
//                         {/*{controlsTestScenarioList.map((scenariosItem) =>*/}
//                         {/*    // <MenuItem value={scenariosItem.id}>{scenariosItem.id}</MenuItem>*/}
//                         {/*    <MenuItem value={scenariosItem}>{scenariosItem}</MenuItem>*/}
//                         {/*)}*/}
//                         {controlsTestScenarioList?.map((scenariosItem) => (
//                             <MenuItem value={scenariosItem}>{scenariosItem}</MenuItem>
//                         ))}
//                     </Select>
//                 </FormControl>
//             {/*}*/}
//             {/*    <br/>*/}
//             {/*    <br/>*/}
//             {/*    <br/>*/}
//                     <Box>
//                 <InputLabel htmlFor="scenarioName"   sx={{ fontSize: 12 }} >Scenario Name (optional)</InputLabel>
//                 <Input sx={{width: 400}}
//                        // defaultValue={controlsTestScenarioName}
//                        value={controlsTestScenarioName ?? ""}
//                        disabled={controlsTestScenarioNumber !=="(new)"}
//                        onChange={(e) => dispatchData(changeWorkQueue({testScenarioName: e.target.value}))}
//                 />
//                     </Box>
//                 </Stack>
//                 {/*<FormHelperText id="expression_helper">enter any logic that will generate the*/}
//                 {/*    value</FormHelperText>*/}
//                 {/*<br/>*/}
//                 <br/>
//                 <br/>
//                 {/*<LocalizationProvider dateAdapter={AdapterDayjs}>*/}
//                 {/*    <DatePicker*/}
//                 {/*        label="Test Period End"*/}
//                 {/*        value={controlsTestScenarioPeriodEnd}*/}
//                 {/*        onChange={(e) => dispatchData(changeWorkQueue({testScenarioPeriodEnd: e.target.value}))}*/}
//
//                 {/*        /!*onChange={(v) => console.log("changed", v)}*!/*/}
//                 {/*        slotProps={{*/}
//                 {/*            textField: {*/}
//                 {/*                variant: "standard",*/}
//                 {/*                onClick: () => console.log("raw picker clicked"),*/}
//                 {/*            },*/}
//                 {/*        }}*/}
//                 {/*    />*/}
//                 {/*</LocalizationProvider>*/}
//                 <DynamicCdpvValueField
//                     disabled={controlsTestScenarioNumber !=="(new)"}
//                     selectorDataPointId = {11614}
//                     selectorValue ={controlsTestScenarioPropertyId}
//                     selectorDataType= "CODE"
//                     selectorContext={"WorkQueueTestScenarioPropertyId"}
//                     selectorLabel={"Test Property"}
//                     autoFocus={false}
//                 />
//                 <br/>
//                 <br/>
//                 <LocalizationProvider dateAdapter={AdapterDayjs}>
//                     <DatePicker
//                         label="Test Period End"
//                         value={
//                             controlsTestScenarioPeriodEnd
//                                 ? dayjs(controlsTestScenarioPeriodEnd)
//                                 : null
//                         }
//                         onChange={(newValue) =>
//                             dispatchData(
//                                 changeWorkQueue({
//                                     testScenarioPeriodEnd: newValue && newValue.isValid()
//                                         ? newValue.format("YYYY-MM-DD")
//                                         : null
//                                 })
//                             )
//                         }
//                         format="MM/DD/YYYY"
//                         slotProps={{
//                             textField: {
//                                 variant: "standard",
//                                 onClick: () => console.log("raw picker clicked"),
//                             },
//                         }}
//                     />
//                 </LocalizationProvider>
//                 {/*<DynamicCdpvValueField*/}
//                 {/*    selectorDataPointId = {538}*/}
//                 {/*    // selectorValue ={controlsTestScenarioPeriodEnd}*/}
//                 {/*    // selectorValue ={null}*/}
//                 {/*    selectorValue ={"06/30/2023"}*/}
//                 {/*    selectorDataType= {"DATE"}*/}
//                 {/*    selectorContext={"WorkQueueTestScenarioPeriodEnd"}*/}
//                 {/*    selectorLabel={"Test Period End"}*/}
//                 {/*    autoFocus={false}*/}
//                 {/*/>*/}
//                 <br/>
//                 <br/>
//                 <br/>
//
//
//                 <br/>
//                     </DialogContent>
//
//                 {/*              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>*/}
//           {/*                  <span>Add</span>*/}
//           {/*                  <TextField*/}
//           {/*                      value={contractCount}*/}
//           {/*                      onChange={(e) => setContractCount(e.target.value)}*/}
//           {/*                      type="number"*/}
//           {/*                      size="small"*/}
//           {/*                      sx={{ width: 90 }}*/}
//           {/*                      inputProps={{ min: 1 }}*/}
//           {/*                  />*/}
//           {/*                  <span>*/}
//           {/*  {selectorContractLevel === 'Guild' && 'Cast-level contract(s)'}*/}
//           {/*                      {selectorContractLevel === 'Cast' && selectorContractGuild === 'SAG' && 'Engagement-level contract(s)'}*/}
//           {/*                      {(selectorContractLevel !== 'Guild' && !(selectorContractLevel === 'Cast' && selectorContractGuild === 'SAG')) && 'contract(s)'}*/}
//           {/*                      {' '}under Contract {selectorContractName} ({selectorContractId}) within {selectorContractGuild} on Product {selectorContractProduct}.*/}
//           {/*</span>*/}
//           {/*              </Box>*/}
//
//             <DialogActions>
//                 <Button onClick={handleConfirmClose} color="primary">
//                     Cancel
//                 </Button>
//                 <Button onClick={handleConfirmTest} color="primary">
//                     Ok
//                 </Button>
//             </DialogActions>
//         </Dialog>
//                 </Box>
// }
//         </Box>
//
//     )
//
// }
